import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  inject,
  signal,
  input,
  output
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonComponent } from '@view/shared/molecules/button/button.component';
import {
  ButtonTypeEnum,
  ButtonSizeEnum
} from '@view/shared/molecules/button/buttonTypesEnum';
import { ModalComponent } from '@view/shared/molecules/modal/modal.component';
import {
  ObservationListItemDTO,
  ReprocessHistoryDTO
} from '@infra-services/observations/api/observations-api.interface';
import { ObservationsPresenter } from '@view/preclinical/presenters/observations.presenter';

@Component({
  selector: 'app-observation-history-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    ModalComponent,
    DatePipe
  ],
  templateUrl: './observation-history-modal.component.html',
  styleUrls: ['./observation-history-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ObservationHistoryModalComponent implements OnInit {
  readonly observation = input.required<ObservationListItemDTO>();
  readonly closeModal = output();
  readonly historyAdded = output();

  private readonly presenter = inject(ObservationsPresenter);

  readonly ButtonTypeEnum = ButtonTypeEnum;
  readonly ButtonSizeEnum = ButtonSizeEnum;

  private readonly _isLoading = signal(false);
  readonly isLoading = this._isLoading.asReadonly();

  private readonly _isSaving = signal(false);
  readonly isSaving = this._isSaving.asReadonly();

  private readonly _historyRecords = signal<ReprocessHistoryDTO[]>([]);
  readonly historyRecords = this._historyRecords.asReadonly();

  private readonly _showAddForm = signal(false);
  readonly showAddForm = this._showAddForm.asReadonly();

  newObservationText = '';

  isResolved(): boolean {
    const currentStatus = this.observation().reprocessStatus?.toUpperCase();
    return currentStatus?.includes('SOLUCIONADO') || false;
  }

  ngOnInit(): void {
    this.loadHistory();
  }

  private loadHistory(): void {
    this._isLoading.set(true);

    this.presenter
      .getObservationHistory(this.observation().observationId)
      .subscribe({
        next: (records) => {
          this._historyRecords.set(records);
          this._isLoading.set(false);
        },
        error: (error) => {
          console.error('Error loading history:', error);
          this._isLoading.set(false);
        }
      });
  }

  onClose(): void {
    this.closeModal.emit();
  }

  toggleAddForm(): void {
    this._showAddForm.set(!this._showAddForm());
    if (!this._showAddForm()) {
      this.newObservationText = '';
    }
  }

  onAddObservation(): void {
    if (!this.newObservationText.trim() || this._isSaving()) {
      return;
    }

    this._isSaving.set(true);

    this.presenter
      .addObservationHistory(
        this.observation().observationId,
        this.newObservationText.trim(),
        this.observation().lgNumber
      )
      .subscribe({
        next: () => {
          this._isSaving.set(false);
          this.newObservationText = '';
          this._showAddForm.set(false);
          this.loadHistory();
          this.historyAdded.emit();
        },
        error: (error) => {
          console.error('Error adding observation:', error);
          this._isSaving.set(false);
        }
      });
  }

  getActionLabel(actionType: string): string {
    switch (actionType) {
      case 'CREATED':
        return 'Creado';
      case 'STATUS_CHANGE':
        return 'Cambio de estado';
      case 'OBSERVATION_ADDED':
        return 'Observación registrada';
      case 'CLOSED':
        return 'Cerrado';
      default:
        return actionType;
    }
  }

  getActionClass(actionType: string): string {
    switch (actionType) {
      case 'CREATED':
        return 'action-created';
      case 'STATUS_CHANGE':
        return 'action-status-change';
      case 'OBSERVATION_ADDED':
        return 'action-observation';
      case 'CLOSED':
        return 'action-closed';
      default:
        return 'action-default';
    }
  }
}
